// ABOUTME: Modal for printing the barcodes of every inbound line at once, with an editable amount per line and workspace selection.
// ABOUTME: Opened from the inbound processing actions menu when the inbound_bulk_barcode_print_enabled setting is on.

import { Box, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { tokens } from "../../../theme";
import SHModal from "../../theme/Modal/SHModal";
import Shbutton from "../../theme/buttons/Shbutton";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import ButtonGroup from "../../theme/buttons/ButtonGroup";
import ShValidatedInput from "../../theme/inputs/ShValidatedInput";
import ShValidatedInputDropdown from "../../theme/dropdowns/ShValidatedInputDropdown";
import { showToastMessage, showToastMessageRequestError } from "../../../utils/toasts";
import { useFindWorkspacesSelectQuery, usePrintBarcodeMutation } from "../../../newapi/warehouse/workspaceSlice";

// Default amount: what is still expected on the line
export const getDefaultPrintAmount = (line) =>
    Math.max((parseInt(line.amountForecasted) || 0) - (parseInt(line.amountReceived) || 0), 0);

const MPrintInboundBarcodes = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [printBarcode] = usePrintBarcodeMutation();
    const { data: workSpaceData, isLoading: workspacesLoading } = useFindWorkspacesSelectQuery();

    const selectedWorkSpace = useSelector((state) => state.settings.workSpace);

    const [amounts, setAmounts] = useState({});
    const [selectedWorkspace, setSelectedWorkspace] = useState(selectedWorkSpace?.id || -1);
    const [workspaces, setWorkspaces] = useState([]);
    const [isPrinting, setIsPrinting] = useState(false);

    const lines = props.inboundlines ?? [];

    useEffect(() => {
        if (!workspacesLoading && workSpaceData) {
            setWorkspaces(workSpaceData.data.map(item => ({
                ...item,
                title: item.name,
                value: item.id
            })));
        }
    }, [workSpaceData, workspacesLoading]);

    useEffect(() => {
        if (props.open) {
            setAmounts(Object.fromEntries(lines.map(line => [line.id, getDefaultPrintAmount(line)])));
            setSelectedWorkspace(selectedWorkSpace?.id || -1);
        }
        // Only reset when the modal opens, so edited amounts survive a refetch of the inbound
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.open]);

    const linesToPrint = lines.filter(line => line.barcodes?.[0] && parseInt(amounts[line.id]) > 0);

    const handlePrint = async () => {
        if (isPrinting) return;
        setIsPrinting(true);

        let failed = 0;
        for (const line of linesToPrint) {
            const description = line.description || '';
            try {
                await printBarcode({
                    amount: parseInt(amounts[line.id]),
                    barcode: line.barcodes[0],
                    productId: line.productId,
                    // Same name length as the single barcode print in the inbound processing view
                    productName: description.length > 23 ? description.substring(0, 23) + '...' : description,
                    workspaceId: selectedWorkspace,
                }).unwrap();
            } catch (err) {
                failed++;
                showToastMessageRequestError(`${t('print_failed')}: ${line.sku ?? line.barcodes[0]}`, t, err);
            }
        }

        setIsPrinting(false);
        if (failed === 0) {
            showToastMessage("success", t('barcodes_printed', { count: linesToPrint.length }));
            props.handleClose();
        }
    };

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    return (
        <SHModal
            open={props.open}
            onClose={props.handleClose}
        >
            <Box
                sx={{
                    textAlign: "center",
                    paddingY: 4,
                    pb:3,
                    paddingX: 5,
                    display: "flex",
                    flexDirection: "column",
                    alignItems:'center',
                }}
            >
                <Subheading>{t("print_all_barcodes")}</Subheading>
                <Text light>{t("print_all_barcodes_description")}</Text>
            </Box>

            <Box
                sx={{
                    background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                    padding: 3,
                    pb:1,
                    borderRadius: 4,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    maxHeight: "50vh",
                    overflowY: "auto",
                }}
            >
                {lines.map(line => {
                    const hasBarcode = !!line.barcodes?.[0];
                    return (
                        <Box
                            key={line.id}
                            sx={{
                                display:"flex",
                                alignItems:'center',
                                justifyContent:"space-between",
                                gap:2,
                                opacity: hasBarcode ? 1 : 0.5,
                            }}
                        >
                            <Box sx={{ minWidth:0 }}>
                                <Text semibold>{line.sku}</Text>
                                <Text light noBreak>{hasBarcode ? `${line.barcodes[0]} · ${line.description ?? ''}` : t("no_barcode")}</Text>
                            </Box>
                            <Box sx={{ width:96, flexShrink:0 }}>
                                <ShValidatedInput
                                    name={`amount_${line.id}`}
                                    disabled={!hasBarcode}
                                    value={amounts[line.id] ?? ''}
                                    onChange={(field, value) => {
                                        const parsed = parseInt(value);
                                        if (value === '' || (!isNaN(parsed) && parsed >= 0)) {
                                            setAmounts(prev => ({ ...prev, [line.id]: value === '' ? '' : parsed }));
                                        }
                                    }}
                                />
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            <Box
                sx={{
                    background: theme.palette.mode === "dark" ? colors.green[100] : colors.green[100],
                    display:"flex",
                    alignItems:'center',
                    borderRadius:4,
                    overflow:'hidden',
                    px:0.5,
                    mt:1.5,
                }}
            >
                <Box
                    sx={{
                        background: theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[0],
                        height:24,
                        borderRadius:3,
                        minWidth:80,
                        textAlign:"center"
                    }}
                >
                    <Text color={colors.txt["primary"]}>{t("workspace")}</Text>
                </Box>
                <ShValidatedInputDropdown
                    name="workspace"
                    displayName="title"
                    changeField="value"
                    selected={workspaces.findIndex(ws => ws.id === selectedWorkspace)}
                    options={workspaces}
                    noSelection={t("select_workspace")}
                    onChange={(field, value) => setSelectedWorkspace(value)}
                />
            </Box>

            <Box
                component="footer"
                sx={{
                    width:1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    display:"flex",
                    alignItems:"center",
                    paddingRight:3,
                    zIndex:1058,
                    position:"relative",
                    paddingTop:2,
                    background:`${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    borderBottomLeftRadius:24,
                    borderBottomRightRadius:24,
                    justifyContent:"space-between",
                    "@media screen and (max-width: 47.9375em)" : {
                        paddingRight:0,
                    }
                }}
            >
                <Shbutton isCancel={true} className={"always-visible"} onClick={props.handleClose} color="primary" variant="text" startIcon={<ArrowRightAltIcon/>}>{t("cancel")}</Shbutton>
                <ButtonGroup version={2}>
                    <Shbutton
                        className={"group-btn"}
                        onClick={handlePrint}
                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                        variant="contained"
                        isLoading={isPrinting}
                        disabled={isPrinting || selectedWorkspace === -1 || linesToPrint.length === 0}
                    >
                        {isPrinting ? t("printing") : t("print")}
                    </Shbutton>
                </ButtonGroup>
            </Box>
        </SHModal>
    );
};

export default MPrintInboundBarcodes;
